import React, { useEffect, useState } from 'react'
import { ref, getDownloadURL } from 'firebase/storage'

import { storage } from '../../FirebaseInit'
import { isImage } from '../../utils/tickets/attachments'

// Render a preview for a single ticket attachment (Req 2.3).
//
// Stored attachment refs carry only { storagePath, fileName, contentType }, so
// this component resolves a download URL from the storagePath via Storage
// getDownloadURL. Image attachments render an <img> preview linking to the
// full file; non-image attachments render a clickable filename link. An
// accessible label is always provided. Access to the file is governed by the
// Storage security rules, so only an admin or the owning customer can load it.
const AttachmentPreview = ({ attachment }) => {
  const [url, setUrl] = useState(attachment?.url || null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false

    if (!attachment || attachment.url || !attachment.storagePath) return

    getDownloadURL(ref(storage, attachment.storagePath))
      .then((resolved) => {
        if (!cancelled) setUrl(resolved)
      })
      .catch((err) => {
        console.error('Failed to resolve attachment URL:', err)
        if (!cancelled) setFailed(true)
      })

    return () => {
      cancelled = true
    }
  }, [attachment])

  if (!attachment) return null

  const { fileName, contentType } = attachment

  // Image attachment with a resolved URL: render an inline preview that links
  // to the full-size file.
  if (isImage(contentType) && url) {
    return (
      <a href={url} target='_blank' rel='noopener noreferrer'>
        <img
          src={url}
          alt={fileName}
          className='max-h-40 rounded border border-gray-100 object-contain'
        />
      </a>
    )
  }

  // Non-image (or not-yet-resolved image) with a URL: clickable download link.
  if (url) {
    return (
      <a
        href={url}
        target='_blank'
        rel='noopener noreferrer'
        className='flex items-center gap-2 rounded border border-gray-100 bg-gray-50 p-2 text-sm text-blue-6 hover:underline'
      >
        <span aria-hidden='true'>📎</span>
        <span>{fileName}</span>
      </a>
    )
  }

  // URL not resolved yet (or failed): show the filename as a placeholder.
  return (
    <div
      className='flex items-center gap-2 rounded border border-gray-100 bg-gray-50 p-2 text-sm text-gray-700'
      aria-label={fileName}
    >
      <span aria-hidden='true'>📎</span>
      <span>{failed ? `${fileName} (unavailable)` : fileName}</span>
    </div>
  )
}

export default AttachmentPreview
